import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getAllAchievements,
  getUnlockedAchievements,
  getPendingAchievements,
  achievementTypeLabels,
  achievementTypeIcons,
  achievementTypeColors,
  levelConfigs,
  listenerAchievementProfile,
  achievementScoreActions,
  achievementDashboardStats,
  formatAchievementCount,
  getProgressPercent,
  type Achievement,
  type AchievementType,
  type AchievementLevel,
  type LevelConfig,
} from '@/mocks/rfwAchievements';

function AchievementCard({ achievement, variant = 'default' }: { achievement: Achievement; variant?: 'default' | 'compact' | 'featured' }) {
  const progress = getProgressPercent(achievement);

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 ${achievement.unlocked ? 'bg-background-50 border-primary-200/60' : 'bg-background-100 border-background-200/70 opacity-70'}`}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${achievement.unlocked ? achievement.bgColor : 'bg-background-200'}`}>
          {achievement.unlocked ? (
            <span className="text-lg">{achievement.emoji}</span>
          ) : (
            <i className="ri-lock-line text-foreground-400"></i>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{achievement.name}</p>
            {achievement.unlocked && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-600">
                <i className="ri-checkbox-circle-fill text-[10px]"></i>
              </span>
            )}
          </div>
          <p className="text-[10px] text-foreground-500 line-clamp-1">{achievement.description}</p>
        </div>
        <div className="text-right shrink-0">
          <p className={`text-xs font-bold ${achievement.unlocked ? 'text-primary-600' : 'text-foreground-400'}`}>+{achievement.pointsReward}</p>
          <p className="text-[9px] text-foreground-400">pts</p>
        </div>
      </div>
    );
  }

  if (variant === 'featured') {
    return (
      <div className={`rounded-2xl border overflow-hidden transition-all duration-300 ${achievement.unlocked ? 'bg-background-50 border-primary-200/60 hover:border-primary-300/60' : 'bg-background-100 border-background-200/70'}`}>
        <div className="p-4 flex flex-col items-center text-center">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ${achievement.unlocked ? achievement.bgColor : 'bg-background-200'}`}>
            {achievement.unlocked ? (
              <span className="text-2xl">{achievement.emoji}</span>
            ) : (
              <i className="ri-lock-line text-xl text-foreground-400"></i>
            )}
          </div>
          <p className="text-sm font-bold text-foreground-900 mb-1">{achievement.name}</p>
          <p className="text-[11px] text-foreground-500 leading-snug mb-3 line-clamp-2">{achievement.description}</p>
          <div className="w-full bg-background-200 rounded-full h-1.5 mb-2">
            <div
              className={`h-full rounded-full transition-all duration-500 ${achievement.unlocked ? 'bg-emerald-500' : 'bg-primary-500'}`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex items-center justify-between w-full">
            <span className="text-[9px] text-foreground-500">{achievement.listenerProgress}/{achievement.progressRequired}</span>
            <span className={`text-[10px] font-bold ${achievement.unlocked ? 'text-emerald-600' : 'text-primary-600'}`}>
              {achievement.unlocked ? 'Completado' : `${progress}%`}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all duration-300 group ${achievement.unlocked ? 'bg-background-50 border-primary-200/60 hover:border-primary-300/60' : 'bg-background-100 border-background-200/70 hover:border-background-300/70'}`}>
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${achievement.unlocked ? achievement.bgColor : 'bg-background-200'}`}>
            {achievement.unlocked ? (
              <span className="text-xl">{achievement.emoji}</span>
            ) : (
              <i className="ri-lock-line text-lg text-foreground-400"></i>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium border ${achievement.unlocked ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-background-200 text-foreground-500 border-background-300'}`}>
              {achievement.unlocked ? 'Desbloqueado' : 'Pendiente'}
            </span>
            {achievement.unlockedDate && (
              <span className="text-[9px] text-foreground-400">{new Date(achievement.unlockedDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
            )}
          </div>
        </div>
        <p className="text-sm font-bold text-foreground-900 mb-1">{achievement.name}</p>
        <p className="text-[11px] text-foreground-500 leading-snug mb-3">{achievement.description}</p>
        <div className="w-full bg-background-200 rounded-full h-2 mb-2">
          <div
            className={`h-full rounded-full transition-all duration-500 ${achievement.unlocked ? 'bg-emerald-500' : 'bg-primary-500'}`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-foreground-500">{achievement.listenerProgress}/{achievement.progressRequired}</span>
          <span className={`text-[11px] font-bold flex items-center gap-0.5 ${achievement.unlocked ? 'text-emerald-600' : 'text-primary-600'}`}>
            <i className="ri-flashlight-line text-[10px]"></i>+{achievement.pointsReward} pts
          </span>
        </div>
      </div>
    </div>
  );
}

function LevelBadge({ config, active = false, achieved = false }: { config: LevelConfig; active?: boolean; achieved?: boolean }) {
  return (
    <div className={`flex flex-col items-center gap-2 p-3 rounded-xl text-center transition-all duration-200 ${active ? 'ring-2 ring-offset-2 ring-primary-500 bg-background-50' : achieved ? 'bg-background-50 border border-emerald-200/70' : 'bg-background-100 border border-background-200/70 opacity-60'}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${active ? config.bgColor : achieved ? config.bgColor : 'bg-background-200'}`}>
        <span className="text-base">{config.emoji}</span>
      </div>
      <p className={`text-[10px] font-bold ${active ? 'text-primary-700' : achieved ? 'text-foreground-900' : 'text-foreground-400'}`}>{config.name}</p>
      <p className="text-[8px] text-foreground-400">{config.minAchievements}+ logros</p>
    </div>
  );
}

export default function LogrosSection() {
  const [activeTab, setActiveTab] = useState<'destacados' | 'niveles'>('destacados');

  const unlockedList = getUnlockedAchievements().slice(0, 6);
  const pendingList = getPendingAchievements().slice(0, 6);
  const currentLevelIdx = levelConfigs.findIndex((l) => l.level === listenerAchievementProfile.currentLevel);

  const tabs = [
    { id: 'destacados' as const, label: 'Logros Destacados', icon: 'ri-star-line', count: getAllAchievements().length },
    { id: 'niveles' as const, label: 'Niveles RFW', icon: 'ri-stack-line', count: levelConfigs.length },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
            SISTEMA DE LOGROS RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Cada Hito <span className="text-primary-600">Se Reconoce</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Primer Seguidor, Primer Saludo, Primera Canción, Primer Concurso, Primer Evento, Primer Club. Cada logro suma puntos y te acerca al siguiente nivel.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Logros Totales', value: achievementDashboardStats.totalAchievements.toString(), icon: 'ri-star-line', color: 'text-primary-600 bg-primary-50' },
            { label: 'Oyentes con Logros', value: formatAchievementCount(achievementDashboardStats.totalListenersWithAchievements), icon: 'ri-user-star-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Logros Desbloqueados', value: formatAchievementCount(achievementDashboardStats.totalUnlockedAchievements), icon: 'ri-checkbox-circle-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Leyendas Diamante', value: achievementDashboardStats.listenersDiamante.toString(), icon: 'ri-vip-crown-line', color: 'text-indigo-600 bg-indigo-50' },
            { label: 'Score Generado', value: formatAchievementCount(achievementDashboardStats.totalScoreGenerated), icon: 'ri-flashlight-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'destacados' && (
          <div className="space-y-8">
            {/* Unlocked Achievements */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-sm text-emerald-600"></i>
                </div>
                <p className="text-sm font-bold text-foreground-900">Logros Obtenidos</p>
                <span className="text-[10px] text-foreground-500">{listenerAchievementProfile.totalAchievements} desbloqueados</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {unlockedList.map((ach) => (
                  <AchievementCard key={ach.id} achievement={ach} />
                ))}
              </div>
            </div>

            {/* Pending Achievements */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-lock-line text-sm text-foreground-500"></i>
                </div>
                <p className="text-sm font-bold text-foreground-900">Logros Pendientes</p>
                <span className="text-[10px] text-foreground-500">{pendingList.length} por desbloquear</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingList.map((ach) => (
                  <AchievementCard key={ach.id} achievement={ach} />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'niveles' && (
          <div className="space-y-8">
            {/* Level Progression */}
            <div className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
              <div className="flex items-center gap-4 mb-6">
                <img src={listenerAchievementProfile.listenerAvatar} alt={listenerAchievementProfile.listenerName} className="w-12 h-12 rounded-full object-cover ring-2 ring-primary-200 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-foreground-900">{listenerAchievementProfile.listenerName}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-foreground-500">Nivel actual:</span>
                    <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-[10px] font-bold">🥇 Oro</span>
                    <span className="text-[10px] text-foreground-500">· {listenerAchievementProfile.currentLevelProgress}% a Platino</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-1">
                {levelConfigs.map((cfg, idx) => {
                  const isAchieved = idx <= currentLevelIdx;
                  const isActive = idx === currentLevelIdx;
                  return (
                    <div key={cfg.level} className="flex-1 flex flex-col items-center gap-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isActive ? cfg.bgColor + ' ring-2 ring-offset-2 ring-primary-500' : isAchieved ? cfg.bgColor : 'bg-background-200'}`}>
                        <span className="text-sm">{cfg.emoji}</span>
                      </div>
                      <p className={`text-[9px] font-bold ${isActive ? 'text-primary-700' : isAchieved ? 'text-foreground-700' : 'text-foreground-300'}`}>{cfg.name}</p>
                      {idx < levelConfigs.length - 1 && (
                        <div className={`w-full h-0.5 rounded-full mt-1 ${isAchieved ? 'bg-emerald-300' : 'bg-background-300'}`}></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Level Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {levelConfigs.map((cfg, idx) => {
                const isAchieved = idx <= currentLevelIdx;
                const isActive = idx === currentLevelIdx;
                return (
                  <div key={cfg.level} className={`p-4 rounded-xl border transition-all duration-200 ${isActive ? 'bg-background-50 border-primary-300/60 ring-1 ring-primary-200/40' : isAchieved ? 'bg-background-50 border-emerald-200/60' : 'bg-background-100 border-background-200/70 opacity-70'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${cfg.bgColor}`}>
                        <span className="text-sm">{cfg.emoji}</span>
                      </div>
                      <div>
                        <p className={`text-xs font-bold ${cfg.textColor}`}>{cfg.name}</p>
                        <p className="text-[9px] text-foreground-500">{cfg.minAchievements}+ logros · {cfg.minScore}+ pts</p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      {cfg.benefits.map((b, bi) => (
                        <div key={bi} className="flex items-center gap-1.5">
                          <i className={`ri-check-line text-[10px] ${isAchieved ? 'text-emerald-500' : 'text-foreground-300'}`}></i>
                          <span className="text-[10px] text-foreground-600">{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/logros"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-trophy-line"></i>
            Explorar Todos los Logros
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-star-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Los logros deben mostrarse públicamente.
          </p>
          <p className="text-xs text-foreground-600">
            Cada logro es un testimonio de tu trayectoria en RFW. No son solo puntos — son hitos que cuentan tu historia como oyente, competidor y miembro de la comunidad.
          </p>
        </div>
      </div>
    </section>
  );
}